import { Question, QuestionContent, isQuestionContent, CodeTests, CodeTestCase } from "@/types/test";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Code, Play, Eye, EyeOff } from "lucide-react";
import { useState } from "react";

interface CodingQuestionEditorProps {
  question: Question;
  onUpdate: (id: string, updates: Partial<Question>) => void;
}

const SUPPORTED_LANGUAGES = [
  { value: "python", label: "Python" },
  { value: "javascript", label: "JavaScript" },
  { value: "java", label: "Java" },
  { value: "cpp", label: "C++" },
  { value: "c", label: "C" },
  { value: "csharp", label: "C#" },
];

export const CodingQuestionEditor = ({ question, onUpdate }: CodingQuestionEditorProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const questionContent = isQuestionContent(question.content) ? question.content : { question: '' };
  const codeTests = question.code_tests || {
    language: "python",
    setup_code: "",
    test_cases: [
      {
        name: "Test Case 1",
        input: "",
        expected_output: "",
        hidden: false,
        weight: 1
      }
    ],
    constraints: {
      time_limit_seconds: 5,
      memory_limit_mb: 128
    }
  };

  const updateContent = (updates: Partial<QuestionContent>) => {
    onUpdate(question.id, {
      content: {
        ...questionContent,
        ...updates,
      },
    });
  };

  const updateCodeTests = (updates: Partial<CodeTests>) => {
    onUpdate(question.id, {
      code_tests: {
        ...codeTests,
        ...updates,
      },
    });
  };

  const addTestCase = () => {
    const newTestCase: CodeTestCase = {
      name: `Test Case ${codeTests.test_cases.length + 1}`,
      input: "",
      expected_output: "",
      hidden: false,
      weight: 1
    };
    
    updateCodeTests({
      test_cases: [...codeTests.test_cases, newTestCase]
    });
  };

  const updateTestCase = (index: number, updates: Partial<CodeTestCase>) => {
    const updatedTestCases = codeTests.test_cases.map((testCase, i) =>
      i === index ? { ...testCase, ...updates } : testCase
    );
    updateCodeTests({ test_cases: updatedTestCases });
  };

  const removeTestCase = (index: number) => {
    if (codeTests.test_cases.length > 1) {
      const updatedTestCases = codeTests.test_cases.filter((_, i) => i !== index);
      updateCodeTests({ test_cases: updatedTestCases });
    }
  };

  return (
    <div className="space-y-6">
      {/* Question Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Coding Question
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="question">Problem Statement</Label>
            <Textarea
              id="question"
              value={questionContent.question || ''}
              onChange={(e) => updateContent({ question: e.target.value })}
              placeholder="Describe the coding problem, requirements, and any examples..."
              className="min-h-[120px] resize-y"
              rows={6}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="language">Programming Language</Label>
              <Select
                value={codeTests.language}
                onValueChange={(value) => updateCodeTests({ language: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="starter-code">Starter Code (Optional)</Label>
              <Textarea
                id="starter-code"
                value={questionContent.starterCode || ''}
                onChange={(e) => updateContent({ starterCode: e.target.value })}
                placeholder="def solution():\n    # Your code here\n    pass"
                className="min-h-[80px] font-mono text-sm"
                rows={3}
              />
            </div>
          </div>

          {codeTests.setup_code !== undefined && (
            <div>
              <Label htmlFor="setup-code">Setup Code (Optional)</Label>
              <Textarea
                id="setup-code"
                value={codeTests.setup_code}
                onChange={(e) => updateCodeTests({ setup_code: e.target.value })}
                placeholder="Any setup code that runs before test cases..."
                className="min-h-[60px] font-mono text-sm"
                rows={2}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Cases */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Test Cases
            </CardTitle>
            <Button onClick={addTestCase} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Test Case
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {codeTests.test_cases.map((testCase, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Input
                        value={testCase.name}
                        onChange={(e) => updateTestCase(index, { name: e.target.value })}
                        className="w-auto font-medium"
                      />
                      <Badge variant={testCase.hidden ? "secondary" : "default"}>
                        {testCase.hidden ? "Hidden" : "Visible"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => updateTestCase(index, { hidden: !testCase.hidden })}
                        size="sm"
                        variant="ghost"
                      >
                        {testCase.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      {codeTests.test_cases.length > 1 && (
                        <Button
                          onClick={() => removeTestCase(index)}
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`input-${index}`}>Input</Label>
                      <Textarea
                        id={`input-${index}`}
                        value={testCase.input}
                        onChange={(e) => updateTestCase(index, { input: e.target.value })}
                        placeholder="Input parameters or data..."
                        className="min-h-[60px] font-mono text-sm"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`output-${index}`}>Expected Output</Label>
                      <Textarea
                        id={`output-${index}`}
                        value={testCase.expected_output}
                        onChange={(e) => updateTestCase(index, { expected_output: e.target.value })}
                        placeholder="Expected result..."
                        className="min-h-[60px] font-mono text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`hidden-${index}`}
                        checked={testCase.hidden}
                        onCheckedChange={(checked) => updateTestCase(index, { hidden: checked })}
                      />
                      <Label htmlFor={`hidden-${index}`}>Hidden from candidates</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`weight-${index}`}>Weight:</Label>
                      <Input
                        id={`weight-${index}`}
                        type="number"
                        min="1"
                        max="10"
                        value={testCase.weight}
                        onChange={(e) => updateTestCase(index, { weight: parseInt(e.target.value) || 1 })}
                        className="w-20"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Constraints */}
      <Card>
        <CardHeader>
          <CardTitle>Execution Constraints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="time-limit">Time Limit (seconds)</Label>
              <Input
                id="time-limit"
                type="number"
                min="1"
                max="30"
                value={codeTests.constraints.time_limit_seconds}
                onChange={(e) => updateCodeTests({
                  constraints: {
                    ...codeTests.constraints,
                    time_limit_seconds: parseInt(e.target.value) || 5
                  }
                })}
              />
            </div>
            <div>
              <Label htmlFor="memory-limit">Memory Limit (MB)</Label>
              <Input
                id="memory-limit"
                type="number"
                min="32"
                max="512"
                value={codeTests.constraints.memory_limit_mb}
                onChange={(e) => updateCodeTests({
                  constraints: {
                    ...codeTests.constraints,
                    memory_limit_mb: parseInt(e.target.value) || 128
                  }
                })}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};